import { NavLink, useParams } from 'react-router-dom';
import { Button, Stack, Typography, Sheet, useTheme, Box } from '@mui/joy';
import { Logo } from './Logo';
import { useMediaQuery } from '@mui/material';
import { ActiveUserMenu } from './ActiveUserMenu';
import { useGetHouseholdsQuery } from "@/features/households/api.js";
import { useMemo } from "react";
import { useGetProfileQuery } from '@/features/onboarding/api';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
import SpokeTwoToneIcon from '@mui/icons-material/SpokeTwoTone';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import ShoppingCartTwoToneIcon from '@mui/icons-material/ShoppingCartTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import CategoryTwoToneIcon from '@mui/icons-material/CategoryTwoTone';
import { ActiveUserAvatarIcon } from './ActiveUserAvatarIcon';
import { PAGE_MAX_WIDTH } from '@/theme.js';

const links = [
  { to: '/recipes', label: 'Recipes', labelMobile: 'Recipes', icon: MenuBookTwoToneIcon },
  { to: '/meals', label: 'Plan', icon: CalendarMonthTwoToneIcon },
  { to: '/shopping-list', label: 'Shopping', icon: ShoppingCartTwoToneIcon },
  { to: '/ingredients', label: 'Ingredients', icon: SpokeTwoToneIcon, requireInstanceAdmin: true, platform: 'desktop' },
  { to: '/categories', label: 'Categories', icon: CategoryTwoToneIcon, requireInstanceAdmin: true, platform: 'desktop' },
  { to: '/users', label: 'Users', icon: PeopleTwoToneIcon, requireHouseholdAdmin: true, platform: 'desktop' },  
  { to: '/my-account', label: 'Profile', icon: ActiveUserAvatarIcon, requireHouseholdAdmin: true, platform: 'mobile' },  
];

function DesktopNavigation({ householdId, availableLinks, fullWidth }) {
  return (
    <Sheet
      sx={{
        zIndex: 1000,
        p: '10.5px',
        px: 3,
        backgroundColor: '#fff',
        borderBottom: '1px solid var(--joy-palette-neutral-300)',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: fullWidth ? 'none' : PAGE_MAX_WIDTH, display: 'flex', margin: '0 auto', flexDirection: 'row', gap: 5 }}>
        <Logo />
        <Stack direction="row" gap={0} sx={{ flex: 1, justifyContent: 'left' }}>
          {availableLinks.map((link) => (
            <NavLink key={link.to} to={`/h/${householdId}${link.to}`} >
              {({ isActive }) => (
                <Button variant="soft" color="plain" size="md" sx={{ 
                  
                  borderRadius: 'var(--joy-radius-sm)',           
                  px: 2, 
                  minHeight: '33px', 
                  ...(isActive && {                                 
                    backgroundColor: 'var(--joy-palette-primary-100)',
                  })
                }}>
                  <Typography level="title-md" sx={{ 
                    fontWeight: '600', 
                    fontSize: '12.5px',                                    
                    color: 'var(--joy-palette-neutral-700)',
                    '&:hover': {
                      color: 'black',
                    },
                    ...(isActive && {
                      color: 'black',
                      fontWeight: '700',                    
                    })                  
                  }}>{link.label}</Typography>
                </Button>              
              )}
            </NavLink>
          ))}          
        </Stack>         
        <ActiveUserMenu />            
      </Box>      
    </Sheet>
  );
}

function MobileNavigation({ householdId, availableLinks, addAction }) {
  const midPoint = Math.floor(availableLinks.length / 2);
  const firstHalf = availableLinks.slice(0, midPoint);
  const secondHalf = availableLinks.slice(midPoint);

  const renderLink = (link) => {
    const Icon = link.icon;
    return (
      <NavLink key={link.to} to={`/h/${householdId}${link.to}`} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        {({ isActive }) => (
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            sx={{
              flexDirection: 'column',
              gap: 0.25,
              px: 0.5,
              py: 0.75,
              minHeight: 'auto',
              width: '100%',
            }}
          >
            <Icon sx={{ fontSize: '24px', color: isActive ? 'primary.500' : 'var(--joy-palette-neutral-500)' }} />
            <Typography sx={{
              fontSize: '10px',
              fontWeight: isActive ? 700 : 500,
              color: isActive ? 'var(--joy-palette-neutral-900)' : 'var(--joy-palette-neutral-600)',
            }}>
              {link.labelMobile || link.label}
            </Typography>
          </Button>
        )}
      </NavLink>          
    );
  };

  return (
    <Sheet
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        p: 0.5,
        display: 'flex',
        flexDirection: 'row',
        gap: 0.5,
        backgroundColor: '#fff',       
        boxShadow: '0 -1px 10px rgba(0, 0, 0, 0.075)',
        alignItems: 'center',
        justifyContent: 'space-around',        
      }}
    >
      {firstHalf.map(renderLink)}
      {addAction && <Box sx={{ flex: 0, display: 'flex', justifyContent: 'center' }}>
        {addAction}
      </Box>}
      {secondHalf.map(renderLink)}
    </Sheet>
  );
}

export function TopNavigation({ addAction, fullWidth }) {
  const { householdId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data: households } = useGetHouseholdsQuery();
  const { data: profile } = useGetProfileQuery();

  const isInstanceAdmin = profile?.data?.instance_role === 'admin';  
  const household = households?.data?.find((household) => household.id === householdId);  

  const availableLinks = useMemo(() => links.filter((link) => {
    if (link.platform == null) {
      return true;
    }    
    return link.platform === (isMobile ? 'mobile' : 'desktop');
  }).filter((link) => {    
    if (link.requireInstanceAdmin) {
      return isInstanceAdmin;
    }

    if (link.requireHouseholdAdmin) {
      return household?.role === 'admin';
    }

    return true;
  }), [household, isInstanceAdmin, isMobile]);

  if (isMobile) {
    return <MobileNavigation householdId={householdId} availableLinks={availableLinks} addAction={addAction} />;
  }

  return <DesktopNavigation householdId={householdId} availableLinks={availableLinks} fullWidth={fullWidth} />;
}
